import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Envelope, Lock, UserCircle, ChalkboardTeacher, Info, ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function SdquLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [role, setRole] = useState("ortu");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const switchRole = (r) => { setRole(r); setIdentifier(""); setPassword(""); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await login(identifier.trim(), password, "sdqu");
      toast.success("Assalamu'alaikum, selamat datang!");
      navigate(data.role === "admin" ? "/sdqu/admin/dashboard" : "/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isGuru = role === "guru";

  return (
    <div className="min-h-screen flex justify-center" style={{ backgroundColor: "#E3F2FD" }}>
      <div className="w-full md:max-w-md min-h-screen flex flex-col px-6 py-10" style={{ backgroundColor: "#E3F2FD" }} data-testid="sdqu-login-screen">
        <button data-testid="sdqu-back-btn" onClick={() => navigate("/welcome")}
          className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center active:scale-90 transition-transform">
          <ArrowLeft size={20} weight="bold" className="text-[#1565C0]" />
        </button>

        <div className="flex flex-col items-center mt-4 mb-6">
          <img src="/sdqu_logo.png" alt="SDQu Muslim Kids"
            className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-amber-400" />
          <h1 className="font-heading text-2xl font-bold text-stone-900 mt-4">Masuk Akun</h1>
          <p className="text-stone-500 text-sm">Silakan pilih jenis akun Anda</p>
        </div>

        {/* White card */}
        <div className="bg-white rounded-3xl shadow-lg p-5">
          {/* Role toggle */}
          <div className="flex gap-2 bg-blue-50 border border-blue-100 rounded-2xl p-1 mb-5">
            <button data-testid="sdqu-role-ortu" onClick={() => switchRole("ortu")}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${!isGuru ? "bg-[#1565C0] text-white shadow" : "text-blue-500"}`}>
              <UserCircle size={17} weight={!isGuru ? "fill" : "regular"} /> Orang Tua
            </button>
            <button data-testid="sdqu-role-guru" onClick={() => switchRole("guru")}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${isGuru ? "bg-[#1565C0] text-white shadow" : "text-blue-500"}`}>
              <ChalkboardTeacher size={17} weight={isGuru ? "fill" : "regular"} /> Guru
            </button>
          </div>

          <motion.form key={role} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="space-y-4">
            <div className="relative">
              <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300" />
              <input
                data-testid="sdqu-login-email-input"
                type="text"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={isGuru ? "Nomor HP" : "Nomor Siswa"}
                className="w-full bg-blue-50/50 border border-blue-100 rounded-2xl py-4 pl-12 pr-4 outline-none focus:border-[#1565C0] transition-colors"
              />
            </div>
            <div className="relative">
              <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300" />
              <input
                data-testid="sdqu-login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Kata sandi"
                className="w-full bg-blue-50/50 border border-blue-100 rounded-2xl py-4 pl-12 pr-4 outline-none focus:border-[#1565C0] transition-colors"
              />
            </div>

            {error && <p data-testid="sdqu-login-error" className="text-red-600 text-sm text-center">{error}</p>}

            <button
              data-testid="sdqu-login-submit-btn"
              disabled={loading}
              className="w-full bg-[#1565C0] text-white font-heading font-semibold text-lg py-4 rounded-full shadow-lg shadow-blue-600/20 active:scale-95 transition-transform disabled:opacity-60"
            >
              {loading ? "Memproses..." : isGuru ? "Masuk sebagai Guru" : "Masuk sebagai Orang Tua"}
            </button>
          </motion.form>

          {!isGuru ? (
            <>
              <p className="text-center mt-5">
                <Link to="/lupa-password" className="text-stone-500 text-sm font-semibold hover:text-[#1565C0]" data-testid="sdqu-goto-forgot-link">Lupa Password?</Link>
              </p>
            </>
          ) : (
            <div data-testid="sdqu-guru-forgot-note" className="mt-5 flex items-center justify-center gap-1.5 text-stone-500 text-sm bg-blue-50 border border-blue-100 rounded-2xl py-3 px-4">
              <Info size={16} weight="fill" className="text-[#1565C0] shrink-0" />
              Lupa Password? Hubungi Admin
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
